// Google sign-in for the React app.
//
// The backend has always had POST /api/auth/google; this page used to say "Google sign-in
// is available on the classic site", which meant anybody whose account was created with
// Google could not sign in here at all. That became load-bearing the moment teammates
// could be invited: an invitation is claimed by EMAIL at sign-in, so a Google user who
// cannot sign in cannot take their seat.
//
// The client id is FETCHED, not baked in at build time. It has to match the one the
// backend verifies the returned ID token against, and two copies of a value that must
// agree is drift waiting to happen — the failure being an "Invalid Google token" with
// nothing on screen to explain it. See GET /api/auth/config for why publishing it is safe.
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

const GIS_SRC = "https://accounts.google.com/gsi/client";
// A stalled request never fires `load` or `error`, so without a deadline the page would say
// "Loading" forever. Generous, because the script is fetched over mobile networks too.
const GIS_TIMEOUT_MS = 15_000;

type GisStatus = "loading" | "ready" | "failed";

interface AuthConfig {
  google: boolean;
  google_client_id: string | null;
}

interface CredentialResponse {
  credential?: string;
}

// The slice of Google Identity Services this file uses. Declared inline rather than pulled
// in as a types package: three calls do not justify a dependency, and the shape is honest
// about how little of that API is touched.
interface GoogleIdentity {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: CredentialResponse) => void;
      }) => void;
      renderButton: (
        parent: HTMLElement,
        options: Record<string, string | number>,
      ) => void;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleIdentity;
  }
}

/** Load the GIS script once per document, however many components ask for it. */
function loadGis(): Promise<void> {
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
  if (existing) {
    return existing.dataset.loaded === "true"
      ? Promise.resolve()
      : new Promise((resolve, reject) => {
          existing.addEventListener("load", () => resolve());
          existing.addEventListener("error", () => reject(new Error("gis")));
        });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => {
      script.dataset.loaded = "true";
      resolve();
    });
    script.addEventListener("error", () => reject(new Error("gis")));
    document.head.appendChild(script);
  });
}

/** Reject if `promise` has not settled within `ms`. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error("gis timeout")), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}

interface Props {
  /** Hands back an ORAI access token. The caller stores it and navigates. */
  onToken: (accessToken: string) => Promise<void> | void;
  onError: (message: string) => void;
}

export function GoogleSignIn({ onToken, onError }: Props) {
  const target = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<GisStatus>("loading");
  // The callback fires from Google's script, outside React's event system, so the newest
  // handlers are read through a ref. Closing over the first render's props would post the
  // credential to a stale callback after any re-render.
  const handlers = useRef({ onToken, onError });
  handlers.current = { onToken, onError };

  const config = useQuery({
    queryKey: ["auth", "config"],
    queryFn: () => apiFetch<AuthConfig>("/api/auth/config"),
    staleTime: Infinity, // changes when the platform is reconfigured, not per visit
    retry: false,
  });

  const clientId = config.data?.google ? config.data.google_client_id : null;

  useEffect(() => {
    if (!clientId || !target.current) return;
    let cancelled = false;

    withTimeout(loadGis(), GIS_TIMEOUT_MS)
      .then(() => {
        if (cancelled || !target.current) return;
        // Blockers often swap Google's script for an empty stand-in: `load` fires but
        // nothing is defined. That is a failure to load, not a slow one.
        if (!window.google) throw new Error("gis missing");
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (!response.credential) {
              handlers.current.onError("Google did not return a sign-in token.");
              return;
            }
            apiFetch<{ access_token: string }>("/api/auth/google", {
              method: "POST",
              body: { credential: response.credential },
            })
              .then((token) => handlers.current.onToken(token.access_token))
              .catch(() =>
                handlers.current.onError(
                  "That Google account could not be signed in. Try email and password, or ask for the invitation to be re-sent.",
                ),
              );
          },
        });
        window.google.accounts.id.renderButton(target.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "continue_with",
          width: 320,
        });
        setStatus("ready");
      })
      .catch(() => {
        // Blocked, stalled or emptied — a corporate proxy, an ad blocker, or no network. Say
        // so rather than promising a button that is never coming.
        if (cancelled) return;
        setStatus("failed");
        handlers.current.onError(
          "Google sign-in could not load — a browser extension or your network may be blocking it. Sign in with email and password instead.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Nothing configured on the platform: render nothing at all. A disabled Google button
  // reads as "broken", while its absence reads as "this account uses a password". A script
  // that failed to load gets the same treatment; the alert above the form says why.
  if (!config.data?.google || status === "failed") return null;

  return (
    <div className="mt-4">
      <div className="mb-3 flex items-center gap-3">
        <div className="h-px flex-1 bg-brand-border" />
        <span className="text-[11px] uppercase tracking-wider text-brand-text-secondary">
          or
        </span>
        <div className="h-px flex-1 bg-brand-border" />
      </div>
      <div ref={target} className="flex justify-center" />
      {status === "loading" && (
        <p className="text-center text-xs text-brand-text-secondary">
          Loading Google sign-in…
        </p>
      )}
    </div>
  );
}
